import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  BROADCAST_EXCLUSION_LABELS,
  formatTemplateRate,
  formatTemplateTotal,
  templateCategoryLabel,
  type BroadcastExclusionReason,
} from "@school-sales-ai/shared";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import { BroadcastStatusBadge } from "@/components/broadcasts/BroadcastStatusBadge";
import {
  useBroadcast,
  useBroadcastRecipients,
  useCancelBroadcast,
} from "@/hooks/useBroadcasts";

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "sent", label: "Enviados" },
  { key: "delivered", label: "Entregados" },
  { key: "failed", label: "Fallidos" },
  { key: "excluded", label: "Excluidos" },
] as const;

function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-ink-100 bg-white px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-ink-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-ink-900">{value}</p>
    </div>
  );
}

export function EnvioDetalle(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<string>("");
  const { data: broadcast, isPending, isError, error } = useBroadcast(id);
  const { data: recipients } = useBroadcastRecipients(id, status);
  const cancel = useCancelBroadcast();

  if (isPending) return <p className="p-6 text-sm text-ink-500">Cargando…</p>;
  if (isError) return <p className="p-6 text-sm text-red-600">{error.message}</p>;

  const isRunning = broadcast.status === "queued" || broadcast.status === "sending";

  return (
    <div className="p-6">
      <Link to="/envios" className="text-sm text-ink-500 underline">
        ← Envíos masivos
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">{broadcast.templateName}</h1>
          <p className="mt-1 text-sm text-ink-700">
            Lanzado por {broadcast.createdByName} ·{" "}
            {new Date(broadcast.createdAt).toLocaleString("es-MX")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <BroadcastStatusBadge status={broadcast.status} />
          {isRunning ? (
            <button
              type="button"
              onClick={() => {
                cancel.mutate(broadcast.id);
              }}
              disabled={cancel.isPending}
              className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Detener envío
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Destinatarios" value={String(broadcast.recipientCount)} />
        <Stat label="Enviados" value={String(broadcast.sentCount)} />
        <Stat label="Fallidos" value={String(broadcast.failedCount)} />
        <Stat label="Excluidos" value={String(broadcast.excludedCount)} />
        <Stat
          label="Costo cobrado (estimado)"
          value={formatTemplateTotal(Number(broadcast.actualCost), broadcast.currency)}
        />
      </div>

      <p className="mt-2 text-xs text-ink-500">
        {formatTemplateRate(Number(broadcast.unitCost), broadcast.currency)} por mensaje ·
        categoría {templateCategoryLabel(broadcast.templateCategory)}. Los montos son estimaciones
        con la tarifa de Meta vigente a {broadcast.pricingAsOf}. El cobro real lo
        determina Meta y se consulta en su panel.
      </p>

      {broadcast.error ? (
        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {broadcast.error}
        </p>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div>
          <h2 className="mb-2 text-sm font-semibold text-ink-700">
            El mensaje que se envió
          </h2>
          {/* The snapshot, not the live template row: Meta allows 10 edits per
              30 days, so re-reading the template would show a message this send
              never carried. */}
          <TemplatePreview
            header={broadcast.templateHeader}
            headerMedia={broadcast.headerMedia}
            body={broadcast.templateBody}
            footer={broadcast.templateFooter}
            examples={[]}
          />
        </div>

        <div>
          <div className="mb-3 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => {
                  setStatus(f.key);
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  status === f.key
                    ? "bg-ink-900 text-white"
                    : "bg-ink-100 text-ink-700 hover:bg-ink-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto rounded-lg border border-ink-100 bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-semibold">Prospecto</th>
                  <th className="px-4 py-2 font-semibold">Teléfono</th>
                  <th className="px-4 py-2 font-semibold">Estado</th>
                  <th className="px-4 py-2 font-semibold">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {(recipients?.items ?? []).map((r) => (
                  <tr key={r.id} className="border-b border-ink-050 last:border-0">
                    <td className="px-4 py-2">
                      {r.leadId ? (
                        <Link
                          to={`/leads/${r.leadId}`}
                          className="text-ink-900 underline decoration-ink-100 underline-offset-2"
                        >
                          {r.leadName ?? r.leadSessionRef}
                        </Link>
                      ) : (
                        (r.leadName ?? r.leadSessionRef)
                      )}
                      <span className="ml-1 text-xs text-ink-500">{r.leadSessionRef}</span>
                    </td>
                    <td className="px-4 py-2 text-ink-700">{r.phoneMasked}</td>
                    <td className="px-4 py-2">
                      <BroadcastStatusBadge status={r.status} kind="recipient" />
                    </td>
                    <td className="px-4 py-2 text-ink-700">
                      {r.excludedReason
                        ? (BROADCAST_EXCLUSION_LABELS[
                            r.excludedReason as BroadcastExclusionReason
                          ] ?? r.excludedReason)
                        : /* Meta's own error_user_msg, verbatim — it is written
                             to be shown, and a translation of ours says less. */
                          (r.metaErrorMessage ?? "—")}
                    </td>
                  </tr>
                ))}
                {(recipients?.items.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-ink-500">
                      Sin destinatarios en esta categoría.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
